"use client";

import React from "react";
import EditSection, { TextEditor, ImageEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input } from "rizzui";
import { PiTrash, PiPlus } from "react-icons/pi";

export default function EducaeetFooterEdit({
  content,
  onContentChange,
}: SectionContentProps) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  const usefulLinks = content.usefulLinks || [
    { label: "Marketplace", href: "#" },
    { label: "Kindergarten", href: "#" },
    { label: "University", href: "#" },
    { label: "GYM Coaching", href: "#" },
    { label: "Cooking", href: "#" }
  ];

  const companyLinks = content.companyLinks || [
    { label: "Contact Us", href: "#" },
    { label: "Become Teacher", href: "#" },
    { label: "Blog", href: "#" },
    { label: "Instructor", href: "#" },
    { label: "Events", href: "#" }
  ];

  const contact = {
    phone: content.contact?.phone || content.phone || "(+91) 123-456-789",
    email: content.contact?.email || content.email || "educaeet@gmail.com",
    location: content.contact?.location || content.contact?.address || content.address || content.location || "North America, USA"
  };

  const handleContactUpdate = (field: "phone" | "email" | "location", val: string) => {
    const updatedContact = { ...contact, [field]: val };
    onContentChange?.({
      ...content,
      contact: updatedContact,
      ...(field === "phone" ? { phone: val } : {}),
      ...(field === "email" ? { email: val } : {}),
      ...(field === "location" ? { address: val, location: val } : {}),
    });
  };

  const socialLinks = {
    twitter: content.socialLinks?.twitter || content.twitter || "",
    facebook: content.socialLinks?.facebook || content.facebook || "",
    instagram: content.socialLinks?.instagram || content.instagram || "",
    linkedin: content.socialLinks?.linkedin || content.linkedin || "",
  };

  const handleSocialUpdate = (platform: "twitter" | "facebook" | "instagram" | "linkedin", val: string) => {
    const updatedSocial = { ...socialLinks, [platform]: val };
    onContentChange?.({
      ...content,
      socialLinks: updatedSocial,
      [platform]: val
    });
  };

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-4">
          {/* Brand & Logo */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Brand & Logo</h4>
            <ImageEditor
              label="Footer Logo Image"
              value={content.logo || ""}
              onChange={(v) => updateContent("logo", v)}
              placeholder="Upload logo or paste image URL"
            />
            <TextEditor
              label="Company / Brand Name"
              value={content.companyName}
              onChange={(v) => updateContent("companyName", v)}
              placeholder="Instacks"
            />
            <TextEditor
              label="Tagline / Description"
              value={content.tagline || content.description}
              onChange={(v) => {
                onContentChange?.({ ...content, tagline: v, description: v });
              }}
              placeholder="Access expert-led courses designed to help you succeed in your career..."
              multiline
            />
            <div className="grid grid-cols-2 gap-2">
              <TextEditor
                label="CTA Button Text"
                value={content.contactCtaText || "Contact Us"}
                onChange={(v) => updateContent("contactCtaText", v)}
                placeholder="Contact Us"
              />
              <TextEditor
                label="CTA Button Link"
                value={content.contactCtaLink || content.contactCtaUrl || "#contact"}
                onChange={(v) => {
                  onContentChange?.({ ...content, contactCtaLink: v, contactCtaUrl: v });
                }}
                placeholder="#contact"
              />
            </div>
          </div>

          {/* Contact Details */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Contact Details</h4>
            <TextEditor
              label="Contact Heading"
              value={content.contactTitle || "Get Contact"}
              onChange={(v) => updateContent("contactTitle", v)}
              placeholder="Get Contact"
            />
            <TextEditor
              label="Phone Number"
              value={contact.phone}
              onChange={(v) => handleContactUpdate("phone", v)}
              placeholder="(+91) 123-456-789"
            />
            <TextEditor
              label="Email Address"
              value={contact.email}
              onChange={(v) => handleContactUpdate("email", v)}
              placeholder="contact@example.com"
            />
            <TextEditor
              label="Location / Address"
              value={contact.location}
              onChange={(v) => handleContactUpdate("location", v)}
              placeholder="City, State, Country"
            />
          </div>

          {/* Social Media Links */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Social Media Links</h4>
            <TextEditor
              label="Twitter / X Profile URL"
              value={socialLinks.twitter}
              onChange={(v) => handleSocialUpdate("twitter", v)}
              placeholder="https://x.com/yourprofile"
            />
            <TextEditor
              label="Facebook Profile URL"
              value={socialLinks.facebook}
              onChange={(v) => handleSocialUpdate("facebook", v)}
              placeholder="https://facebook.com/yourpage"
            />
            <TextEditor
              label="Instagram Profile URL"
              value={socialLinks.instagram}
              onChange={(v) => handleSocialUpdate("instagram", v)}
              placeholder="https://instagram.com/yourprofile"
            />
            <TextEditor
              label="LinkedIn Profile URL"
              value={socialLinks.linkedin}
              onChange={(v) => handleSocialUpdate("linkedin", v)}
              placeholder="https://linkedin.com/company/yourprofile"
            />
          </div>

          {/* Useful Links */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
              <div className="flex-1 mr-2">
                <TextEditor
                  label="Column 1 Title"
                  value={content.usefulLinksTitle || "Useful Links"}
                  onChange={(v) => updateContent("usefulLinksTitle", v)}
                  placeholder="Useful Links"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  updateContent("usefulLinks", [...usefulLinks, { label: "New Link", href: "#" }]);
                }}
                className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 self-end mb-2"
              >
                <PiPlus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            <div className="space-y-3">
              {usefulLinks.map((link: any, idx: number) => (
                <div key={idx} className="flex gap-2 items-center bg-white p-2.5 rounded-lg border border-gray-200 shadow-xs">
                  <div className="flex-1 space-y-1">
                    <Input
                      value={link.label || ""}
                      onChange={(e) => {
                        const newLinks = [...usefulLinks];
                        newLinks[idx] = { ...link, label: e.target.value };
                        updateContent("usefulLinks", newLinks);
                      }}
                      placeholder="Link Label"
                      className="!rounded text-xs h-8"
                    />
                    <Input
                      value={link.href || ""}
                      onChange={(e) => {
                        const newLinks = [...usefulLinks];
                        newLinks[idx] = { ...link, href: e.target.value };
                        updateContent("usefulLinks", newLinks);
                      }}
                      placeholder="https://example.com"
                      className="!rounded text-xs h-8"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newLinks = usefulLinks.filter((_: any, i: number) => i !== idx);
                      updateContent("usefulLinks", newLinks);
                    }}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Delete link"
                  >
                    <PiTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Company Links */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
              <div className="flex-1 mr-2">
                <TextEditor
                  label="Column 2 Title"
                  value={content.companyLinksTitle || "Our Company"}
                  onChange={(v) => updateContent("companyLinksTitle", v)}
                  placeholder="Our Company"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  updateContent("companyLinks", [...companyLinks, { label: "New Link", href: "#" }]);
                }}
                className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 self-end mb-2"
              >
                <PiPlus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            <div className="space-y-3">
              {companyLinks.map((link: any, idx: number) => (
                <div key={idx} className="flex gap-2 items-center bg-white p-2.5 rounded-lg border border-gray-200 shadow-xs">
                  <div className="flex-1 space-y-1">
                    <Input
                      value={link.label || ""}
                      onChange={(e) => {
                        const newLinks = [...companyLinks];
                        newLinks[idx] = { ...link, label: e.target.value };
                        updateContent("companyLinks", newLinks);
                      }}
                      placeholder="Link Label"
                      className="!rounded text-xs h-8"
                    />
                    <Input
                      value={link.href || ""}
                      onChange={(e) => {
                        const newLinks = [...companyLinks];
                        newLinks[idx] = { ...link, href: e.target.value };
                        updateContent("companyLinks", newLinks);
                      }}
                      placeholder="https://example.com"
                      className="!rounded text-xs h-8"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newLinks = companyLinks.filter((_: any, i: number) => i !== idx);
                      updateContent("companyLinks", newLinks);
                    }}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Delete link"
                  >
                    <PiTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
    />
  );
}
